/**
 * 「아직 안 끝난 일」 — 무료 기간, 결제 수단, 사업자등록증
 * ---------------------------------------------------------------------------
 * 값은 전부 `GET /tenant/onboarding` 이 준다. 남은 날짜를 화면에서 세지
 * 않는 것이 요점이다 — 화면이 세면 표준시간대와 경계 처리가 서버·메일과
 * 갈려 **화면은 3일, 메일은 5일**이 된다.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient, type NormalizedError } from '../../lib/api-client';

export interface TrialState {
  active: boolean;
  /** 가장 빨리 끝나는 제품 기준 남은 날. 오늘 끝나면 0 */
  daysLeft: number | null;
  /** YYYY-MM-DD (한국 시간) */
  endsOn: string | null;
  products: string[];
}

export interface OnboardingState {
  trial: TrialState;
  payMethodRegistered: boolean;
  bizLicenseAttached: boolean;
  needsAttention: boolean;
}

export function useOnboarding(enabled = true) {
  return useQuery<OnboardingState, NormalizedError>({
    queryKey: ['tenant', 'onboarding'],
    queryFn: async () => (await apiClient.get<OnboardingState>('/tenant/onboarding')).data,
    enabled,
    // 하루 단위로 바뀌는 값이다. 화면을 옮길 때마다 다시 물을 이유가 없다.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** 무료 기간 딱지에 쓸 문구. 남은 날이 없으면 null — 아무것도 그리지 않는다. */
export function trialLabel(trial: TrialState | undefined): string | null {
  if (!trial?.active || trial.daysLeft === null) return null;
  if (trial.daysLeft === 0) return '무료 사용 · 오늘 종료';
  return `무료 사용 · ${trial.daysLeft}일 남음`;
}

/**
 * 얼마나 급한가.
 *
 * 색만으로 알리지 않는다 — 문구에 날짜가 이미 들어 있다. 색은 눈이 먼저
 * 가게 하는 역할만 한다.
 */
export function trialLevel(trial: TrialState | undefined): 'ok' | 'warning' | 'danger' {
  const d = trial?.daysLeft;
  if (d === null || d === undefined) return 'ok';
  if (d <= 3) return 'danger';
  if (d <= 7) return 'warning';
  return 'ok';
}
